/**
 * Dalle coordinate alla tela: **Web Mercator**, la proiezione delle tessere.
 *
 * Non per amore della cartografia: e' l'unico modo perche' la linea e lo
 * sfondo combacino. Le tessere del mondo (carta o satellite) sono quadrati in
 * Mercator, e una linea proiettata altrimenti scivolerebbe di qualche metro
 * rispetto alla strada che le sta sotto — proprio dove l'occhio confronta.
 * Senza sfondo (rete chiusa, ADR-002) la stessa proiezione disegna la linea
 * da sola, e nessuno vede la differenza.
 *
 * Le misure della tela sono in **pixel del mondo a uno zoom fisso** riportati
 * su cento unita' per lato: cosi' i segni hanno la stessa misura di prima e
 * le tessere sanno dove stare.
 */
import type { MapPoint } from '../lineMap'

/** La tela: cento unita' per lato, sei di margine. */
export const SIDE = 100
export const INSET = 6

/** Lo zoom a cui si misura il mondo: precisione da mezzo metro, che basta. */
const WORLD_ZOOM = 18
const TILE = 256

/** Un punto in pixel del mondo a `WORLD_ZOOM`. */
function world(point: MapPoint): { x: number; y: number } {
  const [lat, lon] = point
  const n = TILE * 2 ** WORLD_ZOOM
  const rad = (Math.max(-85.05, Math.min(85.05, lat)) * Math.PI) / 180
  return {
    x: ((lon + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n,
  }
}

export interface Projection {
  x: (point: MapPoint) => number
  y: (point: MapPoint) => number
  /** Da pixel del mondo (a WORLD_ZOOM) a unita' di tela, e la scala. */
  fromWorld: (x: number, y: number) => { x: number; y: number }
  scale: number
}

/** Proietta le coordinate sulla tela conservando le proporzioni: una linea
 *  che corre da est a ovest non deve sembrare un quadrato. */
export function projectionOf(points: MapPoint[]): Projection {
  const placed = points.map(world)
  const west = Math.min(...placed.map((one) => one.x))
  const east = Math.max(...placed.map((one) => one.x))
  const north = Math.min(...placed.map((one) => one.y))
  const south = Math.max(...placed.map((one) => one.y))
  const width = east - west
  const height = south - north
  const span = SIDE - 2 * INSET
  // Un solo punto non ha estensione: senza questo la scala dividerebbe per
  // zero e il simbolo finirebbe fuori dalla tela.
  const scale = span / Math.max(width, height, 1e-6)
  const padX = (span - width * scale) / 2
  const padY = (span - height * scale) / 2
  const fromWorld = (x: number, y: number): { x: number; y: number } => ({
    x: INSET + padX + (x - west) * scale,
    y: INSET + padY + (y - north) * scale,
  })
  return {
    x: (point) => fromWorld(world(point).x, world(point).y).x,
    y: (point) => fromWorld(world(point).x, world(point).y).y,
    fromWorld,
    scale,
  }
}

export function pathOf(points: MapPoint[], projection: Projection): string {
  return points
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'}${projection.x(point).toFixed(2)},${projection.y(point).toFixed(2)}`,
    )
    .join(' ')
}

/** La finestra sulla tela: quanto se ne vede e da dove. */
export interface Window {
  x: number
  y: number
  size: number
}

export const WHOLE: Window = { x: 0, y: 0, size: SIDE }

/** Avvicina o allontana attorno a un punto della tela, fra tutta la tela e un
 *  sessantaquattresimo di lato: oltre, i segni sono piu' grandi di cio' che
 *  segnano. */
export function zoomed(window: Window, factor: number, at: { x: number; y: number }): Window {
  const size = Math.min(SIDE, Math.max(SIDE / 64, window.size * factor))
  const ratioX = (at.x - window.x) / window.size
  const ratioY = (at.y - window.y) / window.size
  return clamped({ x: at.x - ratioX * size, y: at.y - ratioY * size, size })
}

export function panned(window: Window, dx: number, dy: number): Window {
  return clamped({ x: window.x - dx, y: window.y - dy, size: window.size })
}

function clamped(window: Window): Window {
  const most = SIDE - window.size
  return {
    x: Math.min(most, Math.max(0, window.x)),
    y: Math.min(most, Math.max(0, window.y)),
    size: window.size,
  }
}

export interface Tile {
  key: string
  url: string
  x: number
  y: number
  size: number
}

/**
 * Le tessere che coprono la finestra, allo zoom che le fa comparire grandi
 * circa quanto sono (256 pixel): piu' vicine sarebbero sfocate, piu' lontane
 * sprecherebbero rete. Fuori dalla tela non si chiede niente: e' lo spazio
 * bianco attorno alla linea, e lo sfondo serve dove sta la linea.
 */
export function tilesFor(
  window: Window,
  projection: Projection,
  template: string,
  screenPx: number,
  maxZoom = 19,
): Tile[] {
  if (template === '' || screenPx <= 0) return []
  // Pixel dello schermo per pixel del mondo a WORLD_ZOOM.
  const magnification = (projection.scale * screenPx) / window.size
  const zoom = Math.max(0, Math.min(maxZoom, Math.round(WORLD_ZOOM + Math.log2(magnification))))
  const factor = 2 ** (zoom - WORLD_ZOOM)
  // La finestra in pixel del mondo a WORLD_ZOOM: si inverte `fromWorld`.
  const origin = projection.fromWorld(0, 0)
  const left = (window.x - origin.x) / projection.scale
  const top = (window.y - origin.y) / projection.scale
  const extent = window.size / projection.scale
  const tileWorld = TILE / factor
  const count = 2 ** zoom
  const found: Tile[] = []
  const firstX = Math.max(0, Math.floor(left / tileWorld))
  const lastX = Math.min(count - 1, Math.floor((left + extent) / tileWorld))
  const firstY = Math.max(0, Math.floor(top / tileWorld))
  const lastY = Math.min(count - 1, Math.floor((top + extent) / tileWorld))
  for (let ty = firstY; ty <= lastY; ty += 1) {
    for (let tx = firstX; tx <= lastX; tx += 1) {
      const at = projection.fromWorld(tx * tileWorld, ty * tileWorld)
      found.push({
        key: `${zoom}/${tx}/${ty}`,
        url: template.replace('{z}', String(zoom)).replace('{x}', String(tx)).replace('{y}', String(ty)),
        x: at.x,
        y: at.y,
        size: tileWorld * projection.scale,
      })
    }
  }
  return found
}
